import type { FavoriteItem } from '../types/favorites';
import type { SupportedLanguages } from '../i18n';
import { logger } from '../lib/logger';

interface CalendarEvent {
  title: string;
  description: string;
  location: string;
  startDate: Date;
  endDate: Date;
}

interface CalendarResult {
  success: boolean;
  noEvents: boolean;
  error?: string;
}

/**
 * Adds favorite items with dates to Calendar
 * @param favorites List of favorite items
 * @param config App configuration
 * @param language Current language
 * @returns Result of the calendar operation
 */
export async function addFavoritesToCalendar(
  favorites: FavoriteItem[],
  config: any,
  language: SupportedLanguages
): Promise<CalendarResult> {
  try {
    // Filter favorites that have date and time (events)
    const eventsWithDates = favorites.filter(item => 
      (item.sourceType === 'event' || item.sourceType === 'event-api') && 
      item.data.date && 
      item.data.hour
    );

    // If no events with dates, return early
    if (eventsWithDates.length === 0) {
      return { success: false, noEvents: true };
    }

    // Format events for Calendar
    const calendarEvents = eventsWithDates.map(item => {
      // Get translated description if available
      const description = item.translations?.descriptions?.[language] || item.data.description;
      
      // Parse date and time
      const [year, month, day] = item.data.date.split('-').map(Number);
      const [hour, minute] = item.data.hour.split(':').map(Number);
      
      // Create start date
      const startDate = new Date(year, month - 1, day, hour, minute);
      
      // Create end date based on available information
      let endDate: Date;
      
      if (item.data.end_time) {
        // If end_time is available, use it
        const [endHour, endMinute] = item.data.end_time.split(':').map(Number);
        endDate = new Date(year, month - 1, day, endHour, endMinute);
      } else if (item.data.duration) {
        // If duration is available, add it to start time
        endDate = new Date(startDate);
        endDate.setMinutes(endDate.getMinutes() + Number(item.data.duration));
      } else {
        // Default to 1 hour duration
        endDate = new Date(startDate);
        endDate.setHours(endDate.getHours() + 1);
      }
      
      return {
        title: item.data.name,
        description: `${description}\n\n${item.data.place || ''}\n${item.data.fulladress || ''}`,
        location: item.data.fulladress || item.data.place || '',
        startDate,
        endDate
      };
    });

    // Show the calendar modal with options
    // The actual calendar selection and addition will be handled by the CalendarModal component
    // We'll just return success here since the modal will be shown
    return { success: true, noEvents: false };
  } catch (error) {
    logger.error('Error preparing calendar events', {
      category: 'Calendar',
      data: { error }
    });
    return { 
      success: false, 
      noEvents: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}

/**
 * Generates a Google Calendar URL for adding events
 * @param events List of calendar events
 * @returns Google Calendar URL
 */
export function generateGoogleCalendarUrl(events: CalendarEvent[]): string {
  // Base Google Calendar URL
  const baseUrl = 'https://calendar.google.com/calendar/render';
  
  // For multiple events, we need to create a separate action for each
  const params = events.map(event => {
    // Format dates in the required format: YYYYMMDDTHHMMSS/YYYYMMDDTHHMMSS
    const startFormatted = formatDateForCalendar(event.startDate);
    const endFormatted = formatDateForCalendar(event.endDate);
    
    // Encode event details
    return `action=TEMPLATE&text=${encodeURIComponent(event.title)}&dates=${startFormatted}/${endFormatted}&details=${encodeURIComponent(event.description)}&location=${encodeURIComponent(event.location)}`;
  });
  
  // For multiple events, we need to use the 'wkst=1' parameter
  return `${baseUrl}?${params.join('&')}&wkst=1`;
}

/**
 * Generates an Apple Calendar URL (ics file)
 * @param events List of calendar events
 * @returns URL to download ICS file
 */
export function generateAppleCalendarUrl(events: CalendarEvent[]): string {
  // Create ICS content
  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'PRODID:-//Conciergify//Calendar//EN',
    ...events.flatMap(event => [
      'BEGIN:VEVENT',
      `UID:${generateUid(event.title, event.startDate)}`,
      `DTSTAMP:${formatDateForIcs(new Date())}`,
      `DTSTART:${formatDateForIcs(event.startDate)}`,
      `DTEND:${formatDateForIcs(event.endDate)}`,
      `SUMMARY:${escapeIcsText(event.title)}`,
      `DESCRIPTION:${escapeIcsText(event.description)}`,
      `LOCATION:${escapeIcsText(event.location)}`,
      'END:VEVENT'
    ]),
    'END:VCALENDAR'
  ].join('\r\n');

  // Create a Blob and generate a URL
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  return URL.createObjectURL(blob);
}

/**
 * Generates an Outlook Calendar URL
 * @param events List of calendar events
 * @returns Outlook Calendar URL
 */
export function generateOutlookCalendarUrl(events: CalendarEvent[]): string {
  // For Outlook Web, we'll use the same approach as Google Calendar
  const baseUrl = 'https://outlook.office.com/calendar/0/deeplink/compose';
  
  // For simplicity, we'll just use the first event for Outlook
  // as Outlook doesn't support multiple events in one URL as easily
  const event = events[0];
  
  // Format dates in ISO format
  const startFormatted = event.startDate.toISOString();
  const endFormatted = event.endDate.toISOString();
  
  // Build URL
  const params = new URLSearchParams({
    subject: event.title,
    startdt: startFormatted,
    enddt: endFormatted,
    body: event.description,
    location: event.location,
    path: '/calendar/action/compose',
  });

  return `${baseUrl}?${params.toString()}`;
}

/**
 * Formats a date for Google Calendar URL
 * @param date Date to format
 * @returns Formatted date string (YYYYMMDDTHHMMSS)
 */
function formatDateForCalendar(date: Date): string {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');
  
  return `${year}${month}${day}T${hours}${minutes}${seconds}`;
}

/**
 * Formats a date for ICS file
 * @param date Date to format
 * @returns Formatted date string (YYYYMMDDTHHMMSSZ)
 */
function formatDateForIcs(date: Date): string {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');
  
  return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
}

/**
 * Escapes special characters in text for ICS files
 * @param text Text to escape
 * @returns Escaped text
 */
function escapeIcsText(text: string): string {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');
}

/**
 * Generates a unique ID for an event
 * @param title Event title
 * @param date Event date
 * @returns Unique ID
 */
function generateUid(title: string, date: Date): string {
  const timestamp = date.getTime();
  const random = Math.floor(Math.random() * 1000000);
  return `${timestamp}-${random}-${title.replace(/\W/g, '').substring(0, 10)}@conciergify.app`;
}